/**
 * What the Files, Activity and History tabs say when they are empty —
 * `docs/quiet-records.md` §8, mockup board 6.
 *
 * The quiet panel is Overview's, and only Overview's. **Each tab explains
 * itself in its own words**, because each one is empty for a different
 * reason and only one of the three has anything to offer about it:
 *
 * - **Files** is a thing you do. It gets the drop card, the picker and the
 *   type chips — the tab *is* the target, and the card is the Overview
 *   tile's dashed tile at full size.
 * - **Activity** is a thing you write, and the composer is already at the
 *   top of the tab. The empty state adds a sentence and nothing else: a
 *   button here would scroll somebody to a box already on their screen.
 * - **History** is written by the system. It has no action at all, because
 *   there is nothing on it a person can add — which is worth saying once,
 *   rather than leaving somebody to work out why this tab alone has no
 *   button.
 *
 * The ghost rows the last two carry are `aria-hidden`: they are the shape of
 * what is coming, and a screen reader reading three empty rows is being told
 * about furniture.
 */
import { useState, type ReactNode } from 'react'
import { ACTIVITY_KINDS, FILE_TYPE_LABELS, type FileType } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Button, Chip } from '../components/primitives.js'
import { IconAttachment, IconCamera, IconFile, IconImage } from '../frame/icons.js'
import { KindTile } from './record-overview.js'

/**
 * The five a record offers by default — §11's fourth decision, with its
 * default taken.
 *
 * `other` is deliberately not among them: it is what the guesser falls back
 * to, and a chip that sets a file's type to "Other" is a chip that makes the
 * guess worse.
 */
export const DEFAULT_FILE_TYPES: FileType[] = [
  'photo', 'site_plan', 'contract', 'permit', 'insurance',
]

/**
 * The size the server actually refuses at.
 *
 * The mockup says 100 MB; `MAX_BYTES` in `server/src/routes/files.ts` is 25.
 * A number on a screen that the thing behind it will not honour is worse
 * than no number, so this is the one the server holds — and if that moves,
 * this moves with it.
 */
const MAX_MB = 25

export function FilesEmpty({ phone, canEdit, types, onPick, onPaste }: {
  phone: boolean
  canEdit: boolean
  /** Which chips this record offers — `RecordConfig.fileTypes`, or the five. */
  types: FileType[]
  /** Opens the picker, with a type already decided when a chip was pressed. */
  onPick: (type?: FileType) => void
  /** Absent where the browser has no clipboard read — see `PasteButton`. */
  onPaste?: () => void
}) {
  const [picked, setPicked] = useState<FileType | null>(null)

  if (!canEdit) {
    return (
      <section className="rounded-record border border-line bg-surface px-5 py-8 text-center">
        <b className="block text-[15px] font-semibold text-text">No files yet</b>
        <p className="m-0 mt-1.5 text-body text-text-muted">
          Contracts, site plans and photos filed against this record show up here.
        </p>
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      <section
        className={cn(
          'flex flex-col items-center rounded-record border-[1.5px] border-dashed',
          'border-line-strong bg-surface-alt px-5 text-center',
          phone ? 'py-6' : 'py-9',
        )}
      >
        <span
          aria-hidden
          className="flex h-12 w-12 items-center justify-center rounded-tile-large
                     border border-line bg-surface text-text-muted"
        >
          <IconAttachment size={22} />
        </span>
        <b className="mt-3 text-[15px] font-semibold text-text">
          {phone ? 'Add photos or files' : 'Drop files here'}
        </b>
        <p className="m-0 mt-1 max-w-[420px] text-body leading-[1.45] text-text-muted">
          {phone
            ? 'Take a photo, pick from your library or choose a file.'
            : `Site photos, contracts, plans, permits. Anything up to ${MAX_MB} MB`
              + ' each, as many as you like.'}
        </p>

        {/*
          * A phone cannot drop and has nothing worth opening blind, so the
          * one primary becomes the three gestures it does have — the same
          * three the attach sheet offers, because they are the same three.
          */}
        {phone
          ? (
            <div className="mt-4 flex w-full flex-col gap-2">
              <PhonePick label="Take a photo" icon={<IconCamera size={18} />}
                accept="image/*" capture onPick={onPick} type={picked} />
              <PhonePick label="Photo library" icon={<IconImage size={18} />}
                accept="image/*" onPick={onPick} type={picked} />
              <PhonePick label="Choose a file" icon={<IconFile size={18} />}
                onPick={onPick} type={picked} />
            </div>
          )
          : (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button variant="primary" onClick={() => onPick(picked ?? undefined)}>
                Choose files
              </Button>
              {onPaste && <Button onClick={onPaste}>Paste from clipboard</Button>}
            </div>
          )}
      </section>

      <div className="flex flex-col gap-2">
        <span className="text-label text-text-faint">File type</span>
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
          {types.map((type) => (
            <Chip
              key={type}
              active={picked === type}
              onClick={() => setPicked(picked === type ? null : type)}
              className="shrink-0"
            >
              {FILE_TYPE_LABELS[type]}
            </Chip>
          ))}
        </div>
        <p className="m-0 text-cell leading-[1.45] text-text-muted">
          A chip sets the type for what you pick next; the upload dialog still guesses and
          lets you change each file. Files are Internal until you make one Crew (shows on
          the visit) or Portal (the customer sees it).
        </p>
      </div>
    </div>
  )
}

/**
 * One of a phone's three ways in.
 *
 * Its own `<input>` rather than one whose attributes are swapped: `capture`
 * and `accept` are read when the picker opens, and a React render between
 * the tap and the open is not something this can promise — the same reason
 * `AttachSheet` carries three.
 */
function PhonePick({ label, icon, accept, capture, type, onPick }: {
  label: string
  icon: ReactNode
  accept?: string
  capture?: boolean
  type: FileType | null
  onPick: (type?: FileType) => void
}) {
  return (
    <label
      className="flex h-12 w-full cursor-pointer items-center justify-center gap-2
                 rounded-button border border-line bg-surface text-body font-semibold
                 text-text active:bg-hover"
    >
      {icon}
      {label}
      <input
        type="file"
        multiple={!capture}
        className="hidden"
        {...(accept ? { accept } : {})}
        {...(capture ? { capture: 'environment' as const } : {})}
        onChange={(event) => {
          if (event.target.files?.length) onPick(type ?? undefined)
          event.target.value = ''
        }}
      />
    </label>
  )
}

/**
 * Nothing written down yet — §8. The composer above is the action.
 *
 * **A chip with nothing behind it is a different sentence.** The feed merges
 * what people wrote with what the product did, so on a record made through
 * the app the *All* chip is almost never empty — "Record created" is already
 * on it. The empty state somebody actually meets is the one after pressing
 * **Notes** on a record with no notes, and telling them there what collects
 * here is answering a question they did not ask. The way out of that one is
 * another chip.
 */
export function ActivityEmpty({ subject, filtered }: {
  subject: string
  /** True on any chip but All. */
  filtered?: boolean
}) {
  return (
    <section className="flex flex-col gap-3 rounded-record border border-line bg-surface p-5">
      <div>
        <b className="block text-[15px] font-semibold text-text">
          {filtered ? 'Nothing of that kind yet' : 'Nothing logged yet'}
        </b>
        <p className="m-0 mt-1 text-body leading-[1.45] text-text-muted">
          {filtered
            ? 'Try another chip, or write something in the box above.'
            : `Notes, calls, texts, emails and tasks about ${subject} collect here,`
              + ' newest first.'}
        </p>
      </div>
      <GhostRows />
    </section>
  )
}

/**
 * What History says when there is almost nothing on it — §8.
 *
 * It is **never truly empty**: the audit insert row is always the first line,
 * so the ordinary case is a timeline of one with this underneath it. The
 * "nothing has changed" sentence is for the records that predate the log or
 * came in through an import, where even that row is missing — those are real
 * and saying nothing about them reads as a broken tab.
 */
export function HistoryEmpty({ created }: { created: boolean }) {
  return (
    <section className="flex flex-col gap-3 px-1 py-3">
      {!created && (
        <b className="text-body font-semibold text-text-muted">
          Nothing has changed since it was created.
        </b>
      )}
      <p className="m-0 max-w-[560px] text-cell leading-[1.45] text-text-muted">
        Edits, links added or removed and who made them are written here by the system.
        There is nothing to add by hand, so there is no step on this tab.
      </p>
      <GhostRows count={2} />
    </section>
  )
}

/**
 * The shape of what is coming, and nothing else.
 *
 * `aria-hidden`, like the quiet panel's ghosts: a screen reader reading three
 * empty rows is being told about furniture. The kind tiles cycle through the
 * real ones rather than repeating one, because three identical grey squares
 * read as a loading state and this is not one.
 */
function GhostRows({ count = 3 }: { count?: number }) {
  return (
    <div aria-hidden className="flex flex-col gap-3 opacity-45">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="grayscale">
            <KindTile kind={ACTIVITY_KINDS[index % ACTIVITY_KINDS.length] ?? 'note'} size={28} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="block h-2.5 rounded-full bg-line-soft"
              style={{ width: `${46 - index * 8}%` }} />
            <span className="block h-2 rounded-full bg-line-faint"
              style={{ width: `${28 - index * 5}%` }} />
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Whether this browser can be asked for the clipboard at all.
 *
 * `navigator.clipboard.read` is Chromium and recent Safari; Firefox has only
 * `readText`. A **Paste from clipboard** button that throws on press is worse
 * than one that is not there, so the caller checks this before offering it.
 */
export const clipboardReadable = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.read === 'function'

/**
 * Whatever images are on the clipboard, as files.
 *
 * A screenshot is the one thing people have in hand and no file for, which is
 * the whole reason this button exists. Anything else on the clipboard — text,
 * a URL — is not a file and is skipped rather than turned into one.
 */
export async function clipboardFiles(
  add: (files: File[]) => Promise<void>,
): Promise<void> {
  let items: ClipboardItem[]
  try {
    items = await navigator.clipboard.read()
  } catch {
    /* Refused, or nothing to read. Both mean the same to somebody pressing
       the button, and neither is worth an error card over a picker that is
       two inches away. */
    return
  }

  const files: File[] = []
  for (const item of items) {
    const type = item.types.find((one) => one.startsWith('image/'))
    if (!type) continue
    const blob = await item.getType(type)
    const extension = type.split('/')[1]?.split('+')[0] ?? 'png'
    files.push(new File([blob], `Pasted image.${extension}`, { type }))
  }
  if (files.length > 0) await add(files)
}
